import React, { useEffect } from "react";
import PopImage from "../components/PopImage";
import {
  Page,
  HeroWrap,
  Label,
  Headline,
  SubtitleRow,
  HeroImage,
  SectionHeader,
  ChallengeSection,
  MetricCard,
  SolutionGrid,
  ResultsSection
} from "../components/CaseStudyLayout";

import nbHero from "../images/nb/nb0.png";
import nb1 from "../images/nb/nb1.jpg";
import nb2 from "../images/nb/nb2.jpg";
import nb3 from "../images/nb/nb3.jpg";
import nb4 from "../images/nb/nb4.jpg";
import nb5 from "../images/nb/nb5.jpg";
import nb6 from "../images/nb/nb6.jpg";
import nb7 from "../images/nb/nb7.jpg";
import nb8 from "../images/nb/nb8.jpg";

const NB = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <Page>
      <HeroWrap>
        <Label>MOVEMENT DIRECTION — CAMPAIGN</Label>
        <Headline>New Balance London</Headline>
        <SubtitleRow>
          <p>
            Direction of athletic movement for the New Balance London Marathon campaign.
            We established a visual language of kinetic flow, raw running mechanics, and mid-stride suspension to capture authentic athletic emotion.
          </p>
          <div className="meta">
            <strong>ROLE</strong>
            <span>Movement Director</span>
            <strong>SECTOR</strong>
            <span>Activewear / Commercial</span>
          </div>
        </SubtitleRow>
      </HeroWrap>

      <HeroImage>
        <PopImage src={nbHero} alt="New Balance London Marathon Hero" title="New Balance London Marathon" />
      </HeroImage>

      <ChallengeSection>
        <div className="left">
          <h2>The Brief</h2>
          <p>
            Commercial activewear photography often falls into two traps: feeling overly static and posed, or feeling chaotic and out of focus.
            The challenge was to direct professional models and athletes in a way that showcased the apparel's performance features while capturing the intense, graphic lines of peak physical exertion.
          </p>
          <p>
            My role involved designing kinetic cues, pacing striders, and choreographing specific moments of suspension. 
            We focused on alignment, stride extensions, and raw athletic expression to construct frames that feel both high-fashion and authentically athletic.
          </p>
        </div>
        <div className="right">
          <div className="metrics-box">
            <MetricCard>
              <h3>Models Directed</h3>
              <span>8 PROFESSIONAL ATHLETES</span>
              <p style={{fontSize: '0.85rem', opacity: 0.8, marginTop: '10px'}}>Guided athletic forms to ensure perfect muscle activation and gear visibility.</p>
            </MetricCard>
            <MetricCard>
              <h3>Core Themes</h3>
              <span>KINETIC ARCHITECTURE</span>
              <ul>
                <li>Mid-Air Strides</li>
                <li>Geometric Alignment</li>
                <li>Dynamic Muscle Tension</li>
                <li>Authentic Stride Extension</li>
              </ul>
            </MetricCard>
          </div>
        </div>
      </ChallengeSection>

      <SectionHeader style={{borderBottom: 'none'}}>
        <h2>Campaign Gallery</h2>
        <span>THE MARATHON JOURNEY</span>
      </SectionHeader>

      <div style={{ padding: '0 40px', marginBottom: '60px' }}>
        <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', color: 'var(--accent)', fontWeight: '800' }}>
          Phase 01 — Prep & Warm Up
        </h3>
        <SolutionGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px' }}>
          <div className="pop-image">
            <PopImage src={nb6} alt="Athlete Crouching / Prep" title="Phase 01: Warm Up" />
          </div>
          <div className="pop-image">
            <PopImage src={nb4} alt="Athlete Kneeling / Tying Shoes" title="Phase 01: Preparation" />
          </div>
        </SolutionGrid>
      </div>

      <div style={{ padding: '0 40px', marginBottom: '60px' }}>
        <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', color: 'var(--accent)', fontWeight: '800' }}>
          Phase 02 — Stride & Momentum
        </h3>
        <SolutionGrid style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '30px' }}>
          <div className="wide pop-image">
            <PopImage src={nb2} alt="Athlete Jumping in Air - Orange Jacket" title="Phase 02: Peak Momentum" />
          </div>
          <div className="pop-image">
            <PopImage src={nb1} alt="Athlete Running Side Profile - Black Outfit" title="Phase 02: High Pace" />
          </div>
          <div className="pop-image">
            <PopImage src={nb7} alt="Athlete Running - Light Blue Outfit" title="Phase 02: Momentum" />
          </div>
          <div className="pop-image">
            <PopImage src={nb3} alt="Athlete Running - Coral Shorts" title="Phase 02: Tempo Run" />
          </div>
          <div className="pop-image">
            <PopImage src={nb5} alt="Athlete Jumping - Teal Shorts" title="Phase 02: Flight" />
          </div>
        </SolutionGrid>
      </div>

      <div style={{ padding: '0 40px', marginBottom: '80px' }}>
        <h3 style={{ fontSize: '1.2rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', color: 'var(--accent)', fontWeight: '800' }}>
          Phase 03 — Cool Down & Recovery
        </h3>
        <SolutionGrid style={{ gridTemplateColumns: '1fr', gap: '30px' }}>
          <div className="wide pop-image">
            <PopImage src={nb8} alt="Athlete Stretching - Cool Down" title="Phase 03: Recovery" />
          </div>
        </SolutionGrid>
      </div>

      <ResultsSection>
        <div className="left">
          <h2>Reflections</h2>
          <p>
            Directing movement is about understanding human anatomy as a canvas. 
            By building collaborative workshops with the talent prior to the shoot, we unlocked natural athletic triggers that translated into powerful, crisp, and high-tempo editorial content.
          </p>
        </div>
        <div className="metrics-grid">
          <div className="result-stat">
            <h3>8</h3>
            <h4>Athletes Directed</h4>
            <p>Representing diverse running disciplines</p>
          </div>
          <div className="result-stat">
            <h3>1</h3>
            <h4>Campaign Day</h4>
            <p>Choreography and continuous pacing</p>
          </div>
        </div>
      </ResultsSection>
    </Page>
  );
};

export default NB;
